import {Component, ElementRef, Input, numberAttribute, OnInit, OnDestroy, AfterViewInit, ViewChild, ChangeDetectorRef} from '@angular/core';
import { CommonModule, DatePipe, NgForOf, NgIf, NgClass, KeyValuePipe } from '@angular/common'; // Import CommonModule instead of DatePipe directly
import { Router, RouterLink, ActivatedRoute, RouterOutlet, ParamMap, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { FormArray } from '@angular/forms';
import { NavbarComponent } from '../navbar/navbar.component'; 
import {
  QuestionPoolQuestionDetailComponent
} from "../question-pool-question-detail/question-pool-question-detail.component";
import {DeviceDetectorService} from "ngx-device-detector";
import { Id2Question, RestService } from '../service/rest/rest.service';
import { AuthService } from "../service/auth/auth.service";
import { Question } from '../model/question';
import {timer, Subscription} from "rxjs";
import { LoggerService } from '../service/utils/logger.service';
import { CanDeactivateTest } from '../service/auth/can-deactivate-test.guard';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

const counter = timer(0, 1000);

@Component({
  selector: 'app-question-pool-exam',
  standalone: true,
  imports: [
    NavbarComponent,
    CommonModule,
    RouterLink,
    RouterOutlet,
    QuestionPoolQuestionDetailComponent,
    NgClass,
    NgIf
  ],
  templateUrl: './question-pool-exam.component.html',
  styleUrl: './question-pool-exam.component.css'
})
export class QuestionPoolExamComponent implements OnInit, OnDestroy, AfterViewInit, CanDeactivateTest {
  private _questionId: number = 1;
  private _init?: string = 'init';

  protected answerProgress: number = 0;
  protected answeredCount = 0;
  protected totalQuestions = 0;
  protected timeProgress: number = 0;
  // Exam time limit in seconds, set from subject's exam_time_minutes
  protected examTimeLimit: number = 600; // Default fallback
  protected examTimeLeft: number = 600;
  protected mobile: boolean = false;
  protected questionsMap: Id2Question = { };

  // Pause functionality
  protected pauseUsed: boolean = false;
  protected isPaused: boolean = false;
  protected showPauseConfirm: boolean = false;
  protected pauseTimeLeft: number = 900; // 15 minutes in seconds
  protected pauseTimeLimit: number = 900; // 15 minutes in seconds
  protected savedExamTimeLeft: number = 600; // Store exam time when pausing
  private pauseSubscription?: Subscription;
  
  // Shortcuts modal
  protected showShortcutsModal: boolean = false;
  private finishing: boolean = false;

  // User test settings (shuffle answers)
  userTestSettings: { shuffleQuestionAnswers: boolean; randomizeQuestions: boolean; nextAutomatically: boolean } | null = null;

  // Pagination for question grid when > 50 questions
  protected readonly QUESTIONS_PER_PAGE = 50;
  protected questionGridPage = 1;

  /** Mobile-only bottom-sheet for the question grid. See study component for
   * the rationale — the user shouldn't need to scroll past the question to
   * find Cancel / Finish / navigation. */
  protected gridSheetOpen = false;
  
  // Store bound event handlers for proper cleanup
  private boundKeyboardHandler?: (event: KeyboardEvent) => void;

  // Prevent rapid navigation - use static variables that persist across component instances
  private static isNavigating = false;
  private static lastNavigatedQuestionId: number | null = null;
  private static keyboardListenerAdded = false;
  private static keyboardHandlerInstance: QuestionPoolExamComponent | null = null;
  private static keyboardSeq = 0;

  // container html element reference
  @ViewChild('container') container: ElementRef | undefined;
  @ViewChild(QuestionPoolQuestionDetailComponent) questionDetail!: QuestionPoolQuestionDetailComponent;
  @ViewChild('leaveModalStayBtn') leaveModalStayBtn: ElementRef<HTMLButtonElement> | undefined;
  questionIds: number[] = [];
  timeSubscription: any;

  // question id getter and setter
  @Input({required: true, transform: numberAttribute})
  set questionId(value: number) {
    this._questionId = value;
    this.ensurePageContainsQuestion(value);
    this.loadQuestions();
    // this.answerProgress = this.countAnswerProgress();
  }
  get questionId(): number {
    return this._questionId;
  }

  // init getter and setter
  @Input({required: false/*, transform: numberAttribute*/})
  set init(value: string) {
    this._init = value;

    if (this.init == 'init') {
      this.answerProgress = 0;
      // Reset exam timer when initializing (first exam start)
      this.updateExamTimeLimit(true);
    }
  }
  get init(): string {
    return this._init ? this._init : '0'; // 0 has no function
  }

  handleButtonClick() {
    // Refresh questionsMap from restService to get latest answer state
    this.questionsMap = this.restService.getQuestionsMap();
    this.countAnswerProgress();
  }

  // constructor
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private deviceService: DeviceDetectorService,
    protected restService: RestService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {
    this.mobile = this.deviceService.isMobile();
  }

  // sets counter subscriber
  ngOnInit() {
    // Initialize exam time limit from RestService (subject's exam_time_minutes)
    this.updateExamTimeLimit(true); // Reset timer on first load

    // Store bound handler for proper cleanup
    this.boundKeyboardHandler = this.handleKeyboardShortcuts.bind(this);
    
    // Add keyboard event listener for shortcuts - only once using static flag
    if (!QuestionPoolExamComponent.keyboardListenerAdded) {
      // Use capture phase so we still receive events even if something stops propagation.
      window.addEventListener('keydown', this.boundKeyboardHandler, true);
      QuestionPoolExamComponent.keyboardListenerAdded = true;
      QuestionPoolExamComponent.keyboardHandlerInstance = this;
      console.log('[DEBUG ngOnInit] Exam - Event listeners registered');
    } else {
      // Update the instance reference so the handler uses the current component
      QuestionPoolExamComponent.keyboardHandlerInstance = this;
      console.log('[DEBUG ngOnInit] Exam - Keyboard listener already exists, updated instance reference');
    }
    
    this.timeSubscription = counter.subscribe(() => {
      if (!this.isPaused) {
        this.time();
      }
    });
    this.route.paramMap.subscribe((params: ParamMap) => {
      const newQuestionId = +params.get('questionId')!;
      this.questionId = newQuestionId;
      const initParam = params.get('init') != null ? params.get('init')! : '0';
      this.init = initParam;
      // Clear lastNavigatedQuestionId when route actually changes to prevent blocking
      if (QuestionPoolExamComponent.lastNavigatedQuestionId === newQuestionId) {
        QuestionPoolExamComponent.lastNavigatedQuestionId = null;
      }
      // Only reset timer when init is 'init' (first exam start); never reset when navigating between questions
      this.updateExamTimeLimit(initParam === 'init');
    });

    this.loadUserTestSettings();
  }

  private async loadUserTestSettings() {
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    if (sessionId) {
      this.userTestSettings = await this.restService.ensureUserTestSettingsLoaded(sessionId);
    }
  }

  ngOnDestroy() {
    if (this.timeSubscription) {
      this.timeSubscription.unsubscribe();
    }
    if (this.pauseSubscription) {
      this.pauseSubscription.unsubscribe();
    }
    // Clear instance reference if this is the active instance
    if (QuestionPoolExamComponent.keyboardHandlerInstance === this) {
      // Check if we're navigating away from exam pages
      const currentUrl = this.router.url;
      if (!currentUrl.includes('/question-pool-exam/')) {
        // Remove keyboard listener if navigating away from exam pages
        if (this.boundKeyboardHandler) {
          // Remove both capture and bubble registrations (defensive)
          window.removeEventListener('keydown', this.boundKeyboardHandler, true);
          window.removeEventListener('keydown', this.boundKeyboardHandler);
          QuestionPoolExamComponent.keyboardListenerAdded = false;
          console.log('[DEBUG ngOnDestroy] Exam - Removed keyboard listener (navigating away from exam)');
        }
        QuestionPoolExamComponent.keyboardHandlerInstance = null;
      } else {
        // Keep listener active, just clear instance reference
        // The next component instance will set itself as the handler
        QuestionPoolExamComponent.keyboardHandlerInstance = null;
        console.log('[DEBUG ngOnDestroy] Exam - Cleared instance reference (staying in exam mode)');
      }
    }
  }

  ngAfterViewInit() {
    console.log('[DEBUG ngAfterViewInit] Exam - questionDetail available:', !!this.questionDetail);
    if (this.questionDetail) {
      console.log('[DEBUG ngAfterViewInit] Exam - questionDetail.question available:', !!this.questionDetail.question);
      console.log('[DEBUG ngAfterViewInit] Exam - questionDetailForm available:', !!this.questionDetail.questionDetailForm);
    } else {
      console.log('[DEBUG ngAfterViewInit] Exam - questionDetail is NOT available yet');
    }
  }

  // Helper method to update exam time limit from RestService.
  // Only updates the limit; does NOT reset examTimeLeft (which would happen on every question navigation).
  // examTimeLeft is only set to the limit when init === 'init' (first load).
  private updateExamTimeLimit(resetTimer = false) {
    if (this.restService.currentExamTimeMinutes) {
      this.examTimeLimit = this.restService.currentExamTimeMinutes * 60; // Convert minutes to seconds
      if (resetTimer) {
        this.examTimeLeft = this.examTimeLimit;
      }
      console.log(`[QuestionPoolExamComponent] Updated exam time limit: ${this.examTimeLimit} seconds (${this.restService.currentExamTimeMinutes} minutes), resetTimer=${resetTimer}`);
    } else {
      console.warn(`[QuestionPoolExamComponent] currentExamTimeMinutes not set, using default: ${this.examTimeLimit} seconds`);
    }
  }

  async loadQuestions() {
    // Update exam time limit only (do NOT reset timer - that would reset on every question change)
    this.updateExamTimeLimit(false);
    
    let user = this.authService.getUser();
    const sessionId = user?.sid;
    if (sessionId != null) {
      let questionIds = this.restService.getQuestionIds();
      
      // CRITICAL: Only reload questionIds from backend if they're truly empty AND we have a test ID
      // AND questionIds haven't been loaded for this test yet (to prevent reloading during navigation)
      if (questionIds.length === 0 && this.restService.currentUserTestId && 
          this.restService.currentUserTestId !== this.restService.questionIdsLoadedForTest) {
        console.log('[loadQuestions] Exam - questionIds empty, loading from user test:', this.restService.currentUserTestId);
        const resumeData = await this.restService.getTestForResume(sessionId, this.restService.currentUserTestId);
        if (resumeData && resumeData.questions) {
          // Backend returns questions ordered by questionOrder, which should match original test order
          const allQuestionIds = resumeData.questions.map(q => q.questionId);
          console.log('[loadQuestions] Exam - Loaded questionIds from backend (first 10):', allQuestionIds.slice(0, 10));
          this.restService.setQuestionIdsForResume(allQuestionIds);
          this.restService.setMaxQuestions(allQuestionIds.length);
          questionIds = this.restService.getQuestionIds();
          console.log('[loadQuestions] Exam - Loaded', questionIds.length, 'questionIds from test');
        }
      } else if (questionIds.length > 0) {
        // QuestionIds already loaded - don't reload to preserve order
        console.log('[loadQuestions] Exam - questionIds already loaded (', questionIds.length, 'questions), preserving order');
      } else if (this.restService.currentUserTestId === this.restService.questionIdsLoadedForTest) {
        console.log('[loadQuestions] Exam - questionIds already loaded for this test, skipping reload');
      }
      
      // Update questionIds after potentially loading them
      questionIds = this.restService.getQuestionIds();
      this.questionsMap = this.restService.getQuestionsMap();
      
      // Ensure we're on a valid question from the test
      if (questionIds.length > 0) {
        const currentRouteId = this.route.snapshot.paramMap.get('questionId');
        const currentRouteIdNum = currentRouteId ? +currentRouteId : null;
        
        // Only navigate if current route questionId is not in the test's questionIds
        if (currentRouteIdNum && !questionIds.includes(currentRouteIdNum)) {
          const firstQuestionId = questionIds[0];
          console.log('[loadQuestions] Exam - Current route questionId', currentRouteIdNum, 'not in test, navigating to first question:', firstQuestionId);
          // Navigate to the first question to avoid ghost questions
          this.router.navigate(['/question-pool-exam/', firstQuestionId, '0'], { replaceUrl: true });
          return; // Exit early, navigation will trigger route change and reload
        }
        // Ensure the current question is loaded
        const questionIdToLoad = currentRouteIdNum && questionIds.includes(currentRouteIdNum) ? currentRouteIdNum : questionIds[0];
        await this.restService.getQuestion(questionIdToLoad, sessionId);
      }
      
      // Recalculate progress
      this.countAnswerProgress();
    }
  }
  
  private goToResults(){
    // Results page expects the userTestId, not the current questionId
    const userTestId = this.restService.currentUserTestId;
    if (userTestId) {
      this.router.navigate(
        ['/question-pool-results/', userTestId],
        { replaceUrl: true, state: { skyTransition: true } },
      );
    } else {
      console.error('[goToResults] No userTestId available, cannot navigate to results');
    }
  }
  protected async finishExam(){
    console.log('[finishExam] Finish requested. currentUserTestId:', this.restService.currentUserTestId);
    this.finishing = true;
    // Stop the timer immediately to avoid a second goToResults() firing during navigation.
    if (this.timeSubscription) {
      try {
        this.timeSubscription.unsubscribe();
      } catch {}
      this.timeSubscription = null;
    }
    this.examTimeLeft = 0;
    // Wait for pending answer saves so the report loads with up-to-date backend data
    await this.restService.flushAnswerQueue();
    this.goToResults();
  }

  /** Confirm-before-finish — used by both the desktop "Finish Exam" button
   * and the mobile sticky bar's green check. Only fires when at least one
   * question is unanswered; a fully-answered exam finishes silently. */
  protected showFinishConfirm = false;
  protected finishUnansweredCount = 0;

  protected requestFinish(): void {
    const unanswered = this.getUnansweredCount();
    if (unanswered === 0) {
      void this.finishExam();
      return;
    }
    this.finishUnansweredCount = unanswered;
    this.showFinishConfirm = true;
  }

  protected cancelFinish(): void {
    this.showFinishConfirm = false;
  }

  protected async confirmFinish(): Promise<void> {
    this.showFinishConfirm = false;
    await this.finishExam();
  }

  /** Counts exam questions that haven't been answered yet. */
  protected getUnansweredCount(): number {
    this.questionsMap = this.restService.getQuestionsMap();
    const questionIds = this.restService.getQuestionIds();
    let unanswered = 0;
    for (const id of questionIds) {
      const q = this.questionsMap[id];
      if (!q || q.answered !== true) unanswered++;
    }
    return unanswered;
  }

  /** Mobile users want a confirm on Cancel even with zero answers — thumb
   * mis-tap on the red X is too easy. Desktop keeps the silent-on-empty
   * behavior the web has always had. */
  protected requestCancelFromMobile(): void {
    this.showCancelConfirm = true;
  }

  protected showCancelConfirm = false;

  /** Shown when user tries to leave the page (link, back, etc.) */
  protected showLeaveConfirm = false;
  private pendingLeaveSubject: Subject<boolean> | null = null;
  private pendingLeaveNextUrl: string | null = null;
  private navigatingAfterCancel = false;

  canDeactivate(_currentRoute?: ActivatedRouteSnapshot, _currentState?: RouterStateSnapshot, nextState?: RouterStateSnapshot): Observable<boolean> | boolean {
    if (this.showCancelConfirm) return false;
    if (this.navigatingAfterCancel) return true;
    const questionIds = this.restService.getQuestionIds();
    if (!questionIds.length) return true;
    if (!this.hasAnsweredQuestions()) return true;
    const nextUrl = nextState?.url ?? '';
    if (nextUrl.includes('/question-pool-exam/')) return true;
    if (nextUrl.includes('/question-pool-results/')) return true;
    this.showLeaveConfirm = true;
    this.pendingLeaveNextUrl = (nextState?.url != null ? String(nextState.url) : null);
    this.pendingLeaveSubject = new Subject<boolean>();
    this.cdr.detectChanges();
    setTimeout(() => this.leaveModalStayBtn?.nativeElement?.focus(), 0);
    return this.pendingLeaveSubject.pipe(take(1));
  }

  protected confirmLeavePage(leave: boolean): void {
    this.showLeaveConfirm = false;
    if (leave) {
      this.navigatingAfterCancel = true;
      const nextUrl = this.pendingLeaveNextUrl;
      void this.deleteTestOnly().then(() => {
        if (nextUrl) {
          this.router.navigateByUrl(nextUrl, { replaceUrl: true });
        }
        if (this.pendingLeaveSubject) {
          this.pendingLeaveSubject.next(false);
          this.pendingLeaveSubject.complete();
          this.pendingLeaveSubject = null;
        }
        this.pendingLeaveNextUrl = null;
      });
      return;
    }
    if (this.pendingLeaveSubject) {
      this.pendingLeaveSubject.next(leave);
      this.pendingLeaveSubject.complete();
      this.pendingLeaveSubject = null;
    }
    this.pendingLeaveNextUrl = null;
  }

  protected cancelExam(){
    // Check if any questions were answered
    if (this.hasAnsweredQuestions()) {
      this.showCancelConfirm = true;
    } else {
      this.navigatingAfterCancel = true;
      this.deleteTestAndNavigate();
    }
  }

  protected continueExam(){
    this.showCancelConfirm = false;
  }

  protected cancelWithoutSaving(){
    this.showCancelConfirm = false;
    this.navigatingAfterCancel = true;
    this.deleteTestAndNavigate();
  }

  protected cancelToResumeLater(){
    this.showCancelConfirm = false;
    this.navigatingAfterCancel = true;
    this.router.navigate(['/question-pool'], { replaceUrl: true });
  }

  /** Hard-delete current test only (no navigation). Used when user chooses "Leave" from leave-confirm dialog. */
  private async deleteTestOnly(): Promise<void> {
    const user = this.authService.getUser();
    const sessionId = user?.sid;
    const userTestId = this.restService.currentUserTestId;
    if (sessionId && userTestId) {
      try {
        const success = await this.restService.hardDeleteUserTest(sessionId, userTestId);
        if (success) {
          this.restService.currentUserTestId = null;
          LoggerService.log(`[leavePage] Hard deleted user test ${userTestId}`);
        } else {
          LoggerService.error('Failed to hard delete user test', null);
        }
      } catch (error) {
        LoggerService.error('Error hard deleting user test:', error);
      }
    }
  }

  private async deleteTestAndNavigate(){
    await this.deleteTestOnly();
    this.router.navigate(['/question-pool'], { replaceUrl: true });
  }

  protected hasAnsweredQuestions(): boolean {
    // Update questionsMap from restService to get latest state
    this.questionsMap = this.restService.getQuestionsMap();
    const questionIds = this.restService.getQuestionIds();
    for (const id of questionIds) {
      const question = this.questionsMap[id];
      if (question && question.answered) {
        return true;
      }
    }
    return false;
  }

  // one second time change listener counts left time progress
  private time() {
    if (this.finishing) {
      return;
    }
    if (this.examTimeLeft <= 0) {
      this.timeProgress = 0;
      try {
        this.timeSubscription?.unsubscribe();
      } catch {}
      this.goToResults();
      return;
    }
    this.examTimeLeft--;
    this.timeProgress = Math.round((this.examTimeLeft / this.examTimeLimit) * 100);
    this.restService.setExamTimeProgress(this.timeProgress);
  }

  // Pause functionality
  protected requestPause() {
    if (this.pauseUsed) {
      return; // Already used
    }
    this.showPauseConfirm = true;
  }

  protected cancelPause() {
    this.showPauseConfirm = false;
  }

  protected confirmPause() {
    this.showPauseConfirm = false;
    this.pauseUsed = true;
    this.isPaused = true;
    // Save current exam time
    this.savedExamTimeLeft = this.examTimeLeft;
    // Reset pause timer
    this.pauseTimeLeft = this.pauseTimeLimit;
    
    // Start pause countdown
    this.pauseSubscription = counter.subscribe(() => {
      if (this.isPaused) {
        this.pauseTimeLeft--;
        if (this.pauseTimeLeft <= 0) {
          // Auto-resume when pause time expires
          this.resumeExam();
        }
      }
    });
  }

  protected resumeExam() {
    this.isPaused = false;
    // Restore exam time
    this.examTimeLeft = this.savedExamTimeLeft;
    // Stop pause countdown
    if (this.pauseSubscription) {
      this.pauseSubscription.unsubscribe();
      this.pauseSubscription = undefined;
    }
  }

  protected formatPauseTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  protected formatExamTimeLeft(): string {
    const hours = Math.floor(this.examTimeLeft / 3600);
    const minutes = Math.floor((this.examTimeLeft % 3600) / 60);
    const secs = this.examTimeLeft % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  // counts question progress
  private countAnswerProgress(){
    let answeredAmount = 0;
    const questionIds = this.restService.getQuestionIds(); // Get test question IDs
    const questionsMap = this.restService.getQuestionsMap(); // Get latest questionsMap
    
    // Only count questions that are in the test
    for (const id of questionIds) {
      const question = questionsMap[id];
      if(question && question.answered){
        answeredAmount++;
      }
    }
    
    if (!questionIds.length) {
      this.answerProgress = 0;
    } else {
      this.answerProgress = Math.round(answeredAmount / questionIds.length * 100);
    }

    this.answeredCount = answeredAmount;
    this.totalQuestions = questionIds.length;
    console.log(`[countAnswerProgress] Answered: ${answeredAmount}/${questionIds.length} = ${this.answerProgress}%`);
  }

  // reacts on keyRight and moves to next question
  nextQuestion(event:any) {
    // Prevent rapid navigation using static variable
    if (QuestionPoolExamComponent.isNavigating) {
      console.log('[DEBUG nextQuestion] Exam - Already navigating, skipping');
      return false;
    }
    
    // CRITICAL: Use component.questionId instead of route snapshot
    // The route snapshot can be stale during navigation, but component.questionId
    // is updated via the setter when route params change
    const currentQuestionId = this.questionId;
    
    // Check if we're trying to navigate to the same question we just navigated to
    if (QuestionPoolExamComponent.lastNavigatedQuestionId === currentQuestionId) {
      console.log('[DEBUG nextQuestion] Exam - Already navigated to this question, waiting for route update');
      return false;
    }
    
    console.log('[DEBUG nextQuestion] Exam - current questionId:', currentQuestionId);
    let questionIds = this.restService.getQuestionIds();
    
    if (currentQuestionId && currentQuestionId != questionIds[questionIds.length-1]) {
      const currentIndex = questionIds.findIndex(id => id === currentQuestionId);
      console.log('[DEBUG nextQuestion] Exam - Current index:', currentIndex);
      
      if (currentIndex >= 0 && currentIndex < questionIds.length - 1) {
        const nextId = questionIds[currentIndex + 1];
        console.log('[DEBUG nextQuestion] Exam - Found current at index', currentIndex, 'navigating to nextId:', nextId);
        QuestionPoolExamComponent.isNavigating = true;
        QuestionPoolExamComponent.lastNavigatedQuestionId = nextId;
        // IMPORTANT: exam route uses a second param (init), keep it stable (use '0')
        this.router.navigate(['/question-pool-exam/', '' + nextId, '0'], { replaceUrl: true }).then(() => {
          console.log('[DEBUG nextQuestion] Exam - Navigation complete, resetting flag');
          setTimeout(() => { 
            QuestionPoolExamComponent.isNavigating = false;
            // Reset lastNavigatedQuestionId after a delay to allow route to update
            setTimeout(() => {
              QuestionPoolExamComponent.lastNavigatedQuestionId = null;
            }, 200);
          }, 100);
        }).catch((err) => {
          console.error('[DEBUG nextQuestion] Exam - Navigation error:', err);
          QuestionPoolExamComponent.isNavigating = false;
          QuestionPoolExamComponent.lastNavigatedQuestionId = null;
        });
      }
    }
    event?.preventDefault();
    return false;
  }

  // reacts on keyLeft and moves to previous question
  prevQuestion(event:any) {
    // Prevent rapid navigation using static variable
    if (QuestionPoolExamComponent.isNavigating) {
      console.log('[DEBUG prevQuestion] Exam - Already navigating, skipping');
      return false;
    }
    
    // CRITICAL: Use component.questionId instead of route snapshot
    // The route snapshot can be stale during navigation, but component.questionId
    // is updated via the setter when route params change
    const currentQuestionId = this.questionId;
    
    // Check if we're trying to navigate to the same question we just navigated to
    if (QuestionPoolExamComponent.lastNavigatedQuestionId === currentQuestionId) {
      console.log('[DEBUG prevQuestion] Exam - Already navigated to this question, waiting for route update');
      return false;
    }
    
    console.log('[DEBUG prevQuestion] Exam - current questionId:', currentQuestionId);
    let questionIds = this.restService.getQuestionIds();
    
    if (currentQuestionId) {
      const currentIndex = questionIds.findIndex(id => id === currentQuestionId);
      console.log('[DEBUG prevQuestion] Exam - Current index:', currentIndex);
      
      if (currentIndex > 0) {
        const prevId = questionIds[currentIndex - 1];
        console.log('[DEBUG prevQuestion] Exam - Found current at index', currentIndex, 'navigating to prevId:', prevId);
        QuestionPoolExamComponent.isNavigating = true;
        QuestionPoolExamComponent.lastNavigatedQuestionId = prevId;
        // IMPORTANT: exam route uses a second param (init), keep it stable (use '0')
        this.router.navigate(['/question-pool-exam/', '' + prevId, '0'], { replaceUrl: true }).then(() => {
          console.log('[DEBUG prevQuestion] Exam - Navigation complete, resetting flag');
          setTimeout(() => { 
            QuestionPoolExamComponent.isNavigating = false;
            // Reset lastNavigatedQuestionId after a delay to allow route to update
            setTimeout(() => {
              QuestionPoolExamComponent.lastNavigatedQuestionId = null;
            }, 200);
          }, 100);
        }).catch((err) => {
          console.error('[DEBUG prevQuestion] Exam - Navigation error:', err);
          QuestionPoolExamComponent.isNavigating = false;
          QuestionPoolExamComponent.lastNavigatedQuestionId = null;
        });
      }
    }
    event?.preventDefault();
    return false;
  }

  // reacts on click and focus on container to allow arrows work
  protected containerClick(){
    this.container?.nativeElement.focus();
  }

  showShortcuts() {
    this.showShortcutsModal = true;
  }

  closeShortcuts() {
    this.showShortcutsModal = false;
  }

  protected get usePaginatedGrid(): boolean {
    return this.restService.getQuestionIds().length > this.QUESTIONS_PER_PAGE;
  }

  protected getVisibleQuestionIds(): number[] {
    const ids = this.restService.getQuestionIds();
    if (ids.length <= this.QUESTIONS_PER_PAGE) return ids;
    const start = (this.questionGridPage - 1) * this.QUESTIONS_PER_PAGE;
    return ids.slice(start, start + this.QUESTIONS_PER_PAGE);
  }

  protected getTotalPages(): number {
    const ids = this.restService.getQuestionIds();
    return Math.max(1, Math.ceil(ids.length / this.QUESTIONS_PER_PAGE));
  }

  protected getTotalQuestions(): number {
    return this.restService.getQuestionIds().length;
  }

  protected toggleGridSheet(): void {
    this.gridSheetOpen = !this.gridSheetOpen;
  }

  protected closeGridSheet(): void {
    this.gridSheetOpen = false;
  }

  protected getPageNumbers(): number[] {
    const total = this.getTotalPages();
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, this.questionGridPage - Math.floor(maxVisible / 2));
    const end = Math.min(total, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  protected goToPage(page: number) {
    const total = this.getTotalPages();
    if (page >= 1 && page <= total) this.questionGridPage = page;
  }

  protected getQuestionNumber(questionId: number): number {
    const ids = this.restService.getQuestionIds();
    const idx = ids.findIndex(id => Number(id) === Number(questionId));
    return idx >= 0 ? idx + 1 : 0;
  }

  private ensurePageContainsQuestion(questionId: number) {
    if (!this.usePaginatedGrid) return;
    const ids = this.restService.getQuestionIds();
    const index = ids.findIndex(id => Number(id) === Number(questionId));
    if (index >= 0) {
      const page = Math.floor(index / this.QUESTIONS_PER_PAGE) + 1;
      if (page !== this.questionGridPage) this.questionGridPage = page;
    }
  }

  private handleKeyboardShortcuts(event: KeyboardEvent) {
    // Only log/handle relevant keys
    const isRelevantKey = event.key === 'ArrowLeft' || event.key === 'ArrowRight' || /^[1-4]$/.test(event.key);
    if (!isRelevantKey) return;

    const seq = ++QuestionPoolExamComponent.keyboardSeq;

    // Use the current component instance from static reference
    const component = QuestionPoolExamComponent.keyboardHandlerInstance;
    if (!component) {
      console.log(`[KB-EXAM ${seq}] ignored: no component instance`, { key: event.key, code: (event as any).code });
      return;
    }

    // Check if we're still on an exam route before handling
    const currentUrl = component.router.url;
    if (!currentUrl.includes('/question-pool-exam/')) {
      console.log(`[KB-EXAM ${seq}] ignored: not on exam route`, { key: event.key, url: currentUrl });
      return;
    }
    
    // CRITICAL: Use component.questionId instead of route snapshot
    // The route snapshot can be stale during navigation, but component.questionId
    // is updated via the setter when route params change, ensuring we have the correct current question
    const currentQuestionId = component.questionId;
    
    if (!currentQuestionId) {
      console.log(`[KB-EXAM ${seq}] ignored: no questionId`, { key: event.key, url: currentUrl });
      return;
    }
    
    // CRITICAL: Check if component is still initializing (questionDetail might not be ready yet)
    // Wait a bit if questionDetail is not available or question is not loaded
    if (!component.questionDetail || !component.questionDetail.question) {
      console.log(`[KB-EXAM ${seq}] ignored: questionDetail not ready`, {
        key: event.key,
        url: currentUrl,
        questionId: currentQuestionId,
        hasQuestionDetail: !!component.questionDetail,
        hasQuestion: !!component.questionDetail?.question,
      });
      return;
    }
    
    // CRITICAL: Verify the loaded question matches the current component questionId
    // During navigation, the questionDetail might still show the old question
    const loadedQuestionId = component.questionDetail.question.id;
    if (loadedQuestionId !== currentQuestionId) {
      console.log(`[KB-EXAM ${seq}] ignored: question mismatch`, {
        key: event.key,
        url: currentUrl,
        questionId: currentQuestionId,
        loadedQuestionId,
      });
      return;
    }
    
    // Don't handle shortcuts if modals are open or when typing in input fields
    if (component.showShortcutsModal || component.showCancelConfirm || component.showLeaveConfirm || component.showPauseConfirm || component.showFinishConfirm || component.isPaused) {
      console.log(`[KB-EXAM ${seq}] ignored: modal/pause`, {
        showShortcutsModal: component.showShortcutsModal,
        showCancelConfirm: component.showCancelConfirm,
        showLeaveConfirm: component.showLeaveConfirm,
        showPauseConfirm: component.showPauseConfirm,
        showFinishConfirm: component.showFinishConfirm,
        isPaused: component.isPaused,
      });
      return;
    }

    const target = event.target as HTMLElement | null;
    const activeEl = (typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null);
    const targetTag = target?.tagName?.toUpperCase();
    const activeTag = activeEl?.tagName?.toUpperCase();
    const activeId = activeEl?.id;
    const activeClass = activeEl?.className;

    // Only suppress shortcuts while the user is typing text (checkbox focus should NOT block digits/arrows)
    const isTextEntryTarget = (() => {
      if (!target) return false;
      if ((target as any).isContentEditable) return true;
      if (targetTag === 'TEXTAREA') return true;
      if (targetTag === 'INPUT') {
        const input = target as HTMLInputElement;
        const type = (input.type || '').toLowerCase();
        return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'file', 'color', 'date', 'time'].includes(type);
      }
      return false;
    })();

    console.log(`[KB-EXAM ${seq}] keydown`, {
      key: event.key,
      code: (event as any).code,
      repeat: event.repeat,
      url: currentUrl,
      questionId: currentQuestionId,
      targetTag,
      activeTag,
      activeId,
      activeClass,
      isTextEntryTarget,
    });

    if (isTextEntryTarget) {
      console.log(`[KB-EXAM ${seq}] ignored: typing in text field`);
      return;
    }

    // Arrow keys for navigation
    if (event.key === 'ArrowLeft') {
      console.log(`[KB-EXAM ${seq}] action: ArrowLeft -> prevQuestion`);
      event.preventDefault();
      event.stopPropagation();
      component.prevQuestion(event);
      return;
    } else if (event.key === 'ArrowRight') {
      console.log(`[KB-EXAM ${seq}] action: ArrowRight -> nextQuestion`);
      event.preventDefault();
      event.stopPropagation();
      component.nextQuestion(event);
      return;
    }

    // Number keys (1-4) for answer selection
    if (/^[1-4]$/.test(event.key)) {
      console.log(`[KB-EXAM ${seq}] action: digit`, { digit: event.key });
      
      // At this point we've already verified loadedQuestionId === currentQuestionId above
      // In exam mode, answered questions must remain editable (reselect allowed).
      
      event.preventDefault();
      event.stopPropagation();
      const answerIndex = parseInt(event.key) - 1; // 1 -> 0, 2 -> 1, etc.
      console.log(`[KB-EXAM ${seq}] selecting answerIndex`, { answerIndex });
      
      // Access the question detail component's form
      if (component.questionDetail && component.questionDetail.question && answerIndex < component.questionDetail.question.answers.length) {
        const formArray = component.questionDetail.questionDetailForm.get('answers') as FormArray;
        const control = formArray.at(answerIndex);
        console.log(`[KB-EXAM ${seq}] form control`, {
          hasFormArray: !!formArray,
          hasControl: !!control,
          disabled: (control as any)?.disabled,
          currentValue: (control as any)?.value,
        });
        if (control && !control.disabled) {
          // Treat as radio: pressing 1-4 selects that answer (no toggle-off)
          const newValue = true;
          control.setValue(true);
          console.log(`[KB-EXAM ${seq}] setValue`, { newValue: true });
          // Trigger change event - need to match the format onChange expects
          const answerOption = `answer_${answerIndex}`;
          const changeEvent = { 
            target: { 
              checked: newValue, 
              id: answerOption 
            } 
          } as any;
          console.log(`[KB-EXAM ${seq}] calling questionDetail.onChange`, { id: answerOption, checked: newValue });
          component.questionDetail.onChange(changeEvent);
          console.log(`[KB-EXAM ${seq}] onChange returned`, {
            answered: component.questionDetail.question?.answered,
            correctAnswer: component.questionDetail.question?.correctAnswer,
          });
          
          // CRITICAL: Trigger change detection to update button colors
          // The onChange sets isSubmitted and question.answered, but Angular might not detect the change immediately
          // Use setTimeout to ensure change detection runs after onChange completes
          setTimeout(() => {
            if (component.cdr) {
              component.cdr.detectChanges();
            }
          }, 0);
        } else {
          console.log(`[KB-EXAM ${seq}] ignored: control missing/disabled`);
        }
      } else {
        console.log(`[KB-EXAM ${seq}] ignored: answerIndex out of range`, {
          hasQuestionDetail: !!component.questionDetail,
          hasQuestion: !!component.questionDetail?.question,
          answersLength: component.questionDetail?.question?.answers?.length,
          answerIndex,
        });
      }
    }
  }
}
